import {
  addMonths,
  EMPTY,
  formatMoney,
  formatPercent,
  formatYearMonth,
  type OutstandingRow,
  routes,
  summarizeTurnover,
  todayIso,
  TURNOVER_SOURCES,
  type TurnoverRow,
  type TurnoverSource,
  type YearMonth,
} from '@pmai/contracts';
import {
  Card,
  DataTable,
  EmptyState,
  IconButton,
  InlineCheckbox,
  InlineTextField,
  PageHeader,
  SelectField,
  StatTile,
  StatusBadge,
  ToneBar,
} from '@pmai/ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';

const COMMENT_SAVE_MS = 600;
const SAVED_SHOWN_MS = 1500;

const currentMonth = (): YearMonth => {
  const [year, month] = todayIso().split('-').map(Number) as [number, number];
  return { year, month };
};

/** Completion with a coloured bar: ≥ 75 green, ≥ 40 yellow, otherwise red (FR-TURN-005). */
function Completion({ value, label }: { value: number | null; label: string }) {
  if (value === null) return <span className="text-ink-muted">{EMPTY}</span>;
  const tone = value >= 75 ? 'green' : value >= 40 ? 'yellow' : 'red';
  return (
    <div className="flex min-w-28 items-center gap-2">
      <div className="flex-1">
        <ToneBar value={value} tone={tone} label={label} />
      </div>
      <span className="w-10 text-right text-xs tabular-nums">{formatPercent(value)}</span>
    </div>
  );
}

/** Apgrozījums (FR-TURN-001…010). */
export function TurnoverPage() {
  const { t } = useTranslation();
  const [at, setAt] = useState<YearMonth>(currentMonth);
  const [source, setSource] = useState<TurnoverSource>('both');
  const turnover = useApiQuery(
    routes.turnover.get,
    { query: { year: at.year, month: at.month, source } },
    { placeholderData: (previous) => previous },
  );
  // What the user changed in this month (comment / flag), shown before and after it is saved.
  const [edits, setEdits] = useState<Record<string, { comment?: string; flagged?: boolean }>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const annotate = useApiMutation(routes.turnover.annotate);

  const monthKey = `${at.year}-${at.month}`;
  const editKey = (projectId: string) => `${monthKey}:${projectId}`;
  const server = new Map((turnover.data?.annotations ?? []).map((a) => [a.projectId, a]));
  const comment = (projectId: string) =>
    edits[editKey(projectId)]?.comment ?? server.get(projectId)?.comment ?? '';
  const flagged = (projectId: string) =>
    edits[editKey(projectId)]?.flagged ?? server.get(projectId)?.flagged ?? false;

  const rows = turnover.data?.rows ?? [];
  const summary = summarizeTurnover(rows, new Set(rows.map((r) => r.projectId).filter(flagged)));
  const monthLabel = formatYearMonth(at);
  const f = (field: string) => t(`turnover.fields.${field}`);

  const save = (projectId: string, change: { comment?: string; flagged?: boolean }) => {
    const key = editKey(projectId);
    annotate.mutate(
      { params: { id: projectId }, body: { ...at, ...change } },
      {
        onSuccess: () => {
          setSaved((s) => ({ ...s, [key]: true }));
          setTimeout(() => setSaved((s) => ({ ...s, [key]: false })), SAVED_SHOWN_MS);
        },
      },
    );
  };
  const onComment = (projectId: string, value: string) => {
    const key = editKey(projectId);
    setEdits((e) => ({ ...e, [key]: { ...e[key], comment: value } }));
    clearTimeout(timers.current.get(key));
    timers.current.set(
      key,
      setTimeout(() => save(projectId, { comment: value }), COMMENT_SAVE_MS),
    );
  };
  const onFlag = (projectId: string, value: boolean) => {
    const key = editKey(projectId);
    setEdits((e) => ({ ...e, [key]: { ...e[key], flagged: value } }));
    save(projectId, { flagged: value });
  };

  return (
    <>
      <PageHeader title={t('turnover.title')} />
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconButton
              icon="chevron-left"
              variant="secondary"
              label={t('turnover.prevMonth')}
              onClick={() => setAt((m) => addMonths(m, -1))}
            />
            <span className="min-w-40 text-center text-lg font-semibold" data-testid="turnover-month">
              {monthLabel}
            </span>
            <IconButton
              icon="chevron-right"
              variant="secondary"
              label={t('turnover.nextMonth')}
              onClick={() => setAt((m) => addMonths(m, 1))}
            />
          </div>
          <div className="w-60">
            <SelectField
              label={t('turnover.source')}
              options={TURNOVER_SOURCES.map((s) => ({ value: s, label: t(`turnover.sources.${s}`) }))}
              value={source}
              onChange={(e) => setSource(e.target.value as TurnoverSource)}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <StatTile
            label={t('turnover.totalOf', { source: t(`turnover.sourceTotals.${source}`), month: monthLabel })}
            value={formatMoney(summary.total)}
          />
          <StatTile label={t('turnover.projects')} value={summary.projects} />
          <StatTile
            label={t('turnover.flagged', { count: summary.flaggedProjects })}
            value={formatMoney(summary.flaggedAmount)}
            tone="warning"
          />
        </div>

        <DataTable<TurnoverRow>
          label={t('turnover.title')}
          rows={turnover.data?.rows}
          rowKey={(r) => `${r.projectId}-${r.source}`}
          loading={turnover.isPending}
          empty={
            <EmptyState
              icon="trending-up"
              title={t('turnover.empty', { source: t(`turnover.sourceNames.${source}`) })}
              description={`${t('common.total')}: ${formatMoney('0')}`}
            />
          }
          columns={[
            {
              key: 'project',
              header: f('project'),
              footer: t('turnover.totalRow'),
              cell: (r) => (
                <Link to={`/projects/${r.projectId}`} className="font-medium text-brand-700 hover:underline">
                  {r.projectName}
                </Link>
              ),
            },
            {
              key: 'source',
              header: f('source'),
              cell: (r) => (
                <StatusBadge
                  tone={r.source === 'plan' ? 'info' : 'success'}
                  label={t(`enum.scenario.${r.source}`)}
                />
              ),
            },
            {
              key: 'amount',
              header: f('amount'),
              align: 'right',
              footer: formatMoney(summary.total),
              cell: (r) => formatMoney(r.amount),
            },
            {
              key: 'flagged',
              header: f('flagged'),
              align: 'right',
              cell: (r) => (
                <InlineCheckbox
                  label={`${f('flagged')}: ${r.projectName}`}
                  checked={flagged(r.projectId)}
                  onChange={(e) => onFlag(r.projectId, e.target.checked)}
                />
              ),
            },
            {
              key: 'completion',
              header: f('completion'),
              cell: (r) => <Completion value={r.completion} label={f('completion')} />,
            },
            {
              key: 'comment',
              header: f('comment'),
              className: 'min-w-64',
              cell: (r) => (
                <div className="flex items-center gap-2">
                  <InlineTextField
                    label={`${f('comment')}: ${r.projectName}`}
                    placeholder={t('turnover.commentPlaceholder')}
                    value={comment(r.projectId)}
                    onChange={(e) => onComment(r.projectId, e.target.value)}
                  />
                  <span
                    aria-hidden={!saved[editKey(r.projectId)]}
                    title={t('turnover.saved')}
                    className={saved[editKey(r.projectId)] ? 'text-emerald-600' : 'invisible'}
                  >
                    {t('turnover.savedMark')}
                  </span>
                </div>
              ),
            },
          ]}
        />

        <Outstanding
          rows={turnover.data?.outstanding.rows}
          total={turnover.data?.outstanding.total}
          month={monthLabel}
        />
      </div>
    </>
  );
}

/** "📋 Outstanding Amounts" — invoices planned after the month (FR-TURN-009/010). */
function Outstanding({ rows, total, month }: { rows?: OutstandingRow[]; total?: string; month: string }) {
  const { t } = useTranslation();
  const f = (field: string) => t(`turnover.fields.${field}`);
  return (
    <Card title={t('turnover.outstanding')}>
      <p className="mb-3 text-sm text-ink-muted">{t('turnover.outstandingAfter', { month })}</p>
      <DataTable<OutstandingRow>
        label={t('turnover.outstanding')}
        rows={rows}
        rowKey={(r) => r.projectId}
        empty={<EmptyState icon="file-text" title={t('turnover.outstandingEmpty')} />}
        columns={[
          {
            key: 'project',
            header: f('project'),
            footer: t('turnover.outstandingTotal'),
            cell: (r) => (
              <Link to={`/projects/${r.projectId}`} className="font-medium text-brand-700 hover:underline">
                {r.projectName}
              </Link>
            ),
          },
          {
            key: 'amount',
            header: f('amount'),
            align: 'right',
            footer: total && formatMoney(total),
            cell: (r) => (
              <span className={r.warning ? 'text-ink-muted' : undefined}>{formatMoney(r.amount)}</span>
            ),
          },
          {
            key: 'periods',
            header: f('periods'),
            cell: (r) =>
              r.warning ? (
                <StatusBadge tone="warning" label={t('turnover.noFutureInvoices')} />
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {r.months.map((m) => (
                    <StatusBadge
                      key={`${m.year}-${m.month}`}
                      tone="neutral"
                      label={`${formatYearMonth(m)}: ${formatMoney(m.amount)}`}
                    />
                  ))}
                </span>
              ),
          },
        ]}
      />
    </Card>
  );
}
